import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import logo from './logo.jpg';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f7f8fa;
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 4px 32px rgba(0,0,0,0.09);
  padding: 2.8rem 2.5rem 2.4rem;
  max-width: 480px;
  width: 100%;
  text-align: center;
`;

const Logo = styled.img`
  max-width: 180px;
  margin-bottom: 1.6rem;
`;

const Heading = styled.h1`
  font-size: 1.55rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 0.4rem;
  letter-spacing: -0.3px;
`;

const Subheading = styled.p`
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0 0 1.8rem;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1.5px solid #e2e6ea;
  border-radius: 8px;
  font-size: 0.97rem;
  color: #1a1a2e;
  background: #fafbfc;
  transition: border-color 0.18s, box-shadow 0.18s;
  outline: none;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
    background: #fff;
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const TermsRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  text-align: left;
  cursor: pointer;
  margin-top: 0.2rem;
`;

const Checkbox = styled.input`
  margin-top: 2px;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  accent-color: #2563eb;
  cursor: pointer;
`;

const TermsText = styled.span`
  font-size: 0.82rem;
  color: #9ca3af;
  line-height: 1.5;

  a {
    color: #6b7280;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: #2563eb;
    }
  }
`;

const Button = styled.button`
  padding: 0.8rem;
  background-color: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
  margin-top: 0.3rem;
  letter-spacing: 0.01em;

  &:hover:not(:disabled) {
    background-color: #1d4ed8;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.93rem;
  color: ${props => props.error ? '#b91c1c' : '#15803d'};
  background-color: ${props => props.error ? '#fef2f2' : '#f0fdf4'};
  border: 1px solid ${props => props.error ? '#fecaca' : '#bbf7d0'};
`;

/* Honeypot — visually hidden from real users */
const HoneypotField = styled.input`
  position: absolute;
  left: -9999px;
  top: -9999px;
  opacity: 0;
  height: 0;
  width: 0;
  pointer-events: none;
  tab-index: -1;
`;

const Footer = styled.footer`
  background: #fff;
  border-top: 1px solid #e9ecef;
  padding: 1.1rem 1rem;
  text-align: center;
`;

const FooterText = styled.p`
  font-size: 0.8rem;
  color: #9ca3af;
  margin: 0;

  a {
    color: #6b7280;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      color: #2563eb;
      text-decoration: underline;
    }
  }
`;

function App() {
  const [formData, setFormData] = useState({ firstName: '', email: '' });
  const [honeypot, setHoneypot] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadTimeRef = useRef(Date.now());

  useEffect(() => {
    loadTimeRef.current = Date.now();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (honeypot) return;

    const elapsed = Date.now() - loadTimeRef.current;
    if (elapsed < 3000) return;

    if (!agreed) {
      setError('Please agree to the terms before subscribing.');
      setMessage(null);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName: formData.firstName, email: formData.email }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage('Thank you for subscribing to our newsletter!');
        setError(null);
        setFormData({ firstName: '', email: '' });
        setAgreed(false);
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
        setMessage(null);
      }
    } catch (err) {
      setError('Unable to connect to the server. Please try again later.');
      setMessage(null);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const currentYear = new Date().getFullYear();

  return (
    <PageWrapper>
      <Main>
        <Card>
          <Logo src={logo} alt="BordTek Logo" />
          <Heading>Stay in the Loop</Heading>
          <Subheading>
            Get the latest updates, product news, and industry insights from BordTek Controls delivered straight to your inbox.
          </Subheading>
          <Form onSubmit={handleSubmit}>
            {/* Honeypot — bots fill this, humans don't see it */}
            <HoneypotField
              type="text"
              name="website"
              value={honeypot}
              onChange={e => setHoneypot(e.target.value)}
              autoComplete="off"
              tabIndex="-1"
            />
            <Input
              type="text"
              name="firstName"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleChange}
              required
              autoComplete="given-name"
            />
            <Input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />
            <TermsRow>
              <Checkbox
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                required
              />
              <TermsText>
                I agree to receive email communications from BordTek Controls Inc. and understand I can unsubscribe at any time. We respect your privacy and will never share your information.
              </TermsText>
            </TermsRow>
            <Button type="submit" disabled={loading || !agreed}>
              {loading ? 'Subscribing…' : 'Subscribe'}
            </Button>
          </Form>
          {message && <Message>{message}</Message>}
          {error && <Message error>{error}</Message>}
        </Card>
      </Main>
      <Footer>
        <FooterText>
          &copy; {currentYear} <a href="https://bordtek.com" target="_blank" rel="noopener noreferrer">BordTek Controls Inc.</a> &mdash; All rights reserved.
        </FooterText>
      </Footer>
    </PageWrapper>
  );
}

export default App;
